import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import SectionPicker from '../../reusable/SectionPicker/SectionPicker';
import { useShellAsideControl } from '../../layout/ShellAside';
import { t } from '../../i18n/i18n';
import useAffiliateAccess from './useAffiliateAccess';
import './AffiliateAside.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * The section nav, in the frame's own order. "Dashboard" is the section's
 * index (Partner Programme) — the design labels it by its role inside
 * Affiliate, not by the page's own title.
 */
export const SECTIONS = [
  { to: '/affiliate', label: 'Dashboard', icon: 'affiliate-nav-dashboard', end: true },
  { to: '/affiliate/giveaway', label: 'Giveaway', icon: 'affiliate-nav-giveaway' },
  { to: '/affiliate/winner-submission', label: 'Winner Submission', icon: 'affiliate-nav-winner' },
  { to: '/affiliate/personal-account', label: 'Personal Account', icon: 'affiliate-nav-personal' },
  { to: '/affiliate/retainer', label: 'Retainer', icon: 'affiliate-nav-retainer' },
  // The live dashboard's Affiliate Marketing page — Overview, Affiliate Codes
  // and Commission Payouts as tabs inside it.
  { to: '/affiliate/marketing', label: 'Affiliate Marketing', icon: 'affiliate-nav-marketing' },
];

/** The rule NavLink applies, so the fill can't disagree with which row is lit. */
const isCurrent = (section, pathname) =>
  section.end
    ? pathname === section.to
    : pathname === section.to || pathname.startsWith(`${section.to}/`);

/**
 * Aside - Affiliate programme, 1:16694.
 *
 * Published into the shell rather than rendered inside the page, because the
 * frame puts it flush against the 76px rail at full height with the top bar
 * starting after it — see `useShellAside`. It is also why Affiliate is one of
 * the rail routes: this aside stands in for the expanded sidebar.
 */
export default function AffiliateAside() {
  const { pathname } = useLocation();
  const { isAsideCollapsed, toggleAside } = useShellAsideControl();
  const [tip, setTip] = useState(null);

  const isRail = Boolean(isAsideCollapsed);

  useEffect(() => {
    if (!isRail) setTip(null);
  }, [isRail]);

  const peek = (event) => {
    if (!isRail) return;
    const row = event.target.closest?.('.affiliate-aside__link');
    const label = row?.dataset?.label;
    if (!label) return;

    const rect = row.getBoundingClientRect();
    setTip({ label, x: rect.right + 10, y: rect.top + rect.height / 2 });
  };

  /* One fill that travels between rows rather than each row painting its own,
     so moving between sections slides the highlight instead of cutting it. The
     shell's sidebar does the same thing by measuring, because a group opening
     there moves the rows under it; nothing here changes height, so this can
     work the way the dock does — from an index, with CSS turning it into a
     distance.
     
     Placing it from the index also means it is already where it belongs at the
     first paint, with no earlier position to travel from. */
  // Only the pages this affiliate gets (see useAffiliateAccess).
  const { canSee } = useAffiliateAccess();
  const sections = SECTIONS.filter((section) => canSee(section.to));
  const index = sections.findIndex((section) => isCurrent(section, pathname));

  /* Where it rests if a route under /affiliate ever falls outside this list:
     it fades where it stands instead of sliding back to the first row. */
  const [resting, setResting] = useState(index < 0 ? 0 : index);

  useEffect(() => {
    if (index >= 0) setResting(index);
  }, [index]);

  const at = index < 0 ? resting : index;

  return (
    <aside className={`affiliate-aside shell__aside${isRail ? ' affiliate-aside--rail' : ''}`}>
      <div className="affiliate-aside__head">
        <div className="affiliate-aside__head-copy">
          <h2 className="affiliate-aside__title">{t('Affiliate')}</h2>
          <p className="affiliate-aside__subtitle">{t('Partner programme')}</p>
        </div>
        <button
          type="button"
          className="affiliate-aside__collapse"
          onClick={toggleAside}
          aria-expanded={!isRail}
          aria-label={isRail ? t('Expand affiliate menu') : t('Collapse affiliate menu')}
          title={isRail ? t('Expand affiliate menu') : t('Collapse affiliate menu')}
        >
          <span className="icon" style={icon('sidebar-collapse')} />
        </button>
      </div>

      {/* Five rows don't fit across a phone, so the same five become a picker.
          It is rendered alongside the list rather than swapped in at a JS
          breakpoint, so only CSS decides which one is live. */}
      <SectionPicker
        sections={sections.map((section) => ({ ...section, label: t(section.label) }))}
        label={t('Affiliate section')}
      />

      <nav
        className="affiliate-aside__nav"
        onMouseOver={peek}
        onMouseOut={() => setTip(null)}
        onFocus={peek}
        onBlur={() => setTip(null)}
        onScroll={() => setTip(null)}
      >
        {/* Inside the list, so it scrolls with the rows it marks. */}
        <span
          className={`affiliate-aside__fill${index >= 0 ? ' is-on' : ''}`}
          style={{ '--fill-i': at }}
          aria-hidden="true"
        />

        {sections.map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            end={section.end}
            data-label={t(section.label)}
            title={isRail ? t(section.label) : undefined}
            className={({ isActive }) =>
              `affiliate-aside__link${isActive ? ' is-active' : ''}`
            }
          >
            <span className="icon affiliate-aside__icon" style={icon(section.icon)} />
            <span className="affiliate-aside__label">{t(section.label)}</span>
          </NavLink>
        ))}
      </nav>

      {isRail && tip &&
        createPortal(
          <div
            className="rail-tip"
            style={{ top: `${tip.y}px`, left: `${tip.x}px` }}
            aria-hidden="true"
          >
            {tip.label}
          </div>,
          document.body,
        )}

      <div className="affiliate-aside__promo-wrap">
        <div
          className="affiliate-promo"
          style={{ '--promo-bg': 'url(/icons/affiliate-aside-bg.svg)' }}
        >
          <div className="affiliate-promo__brand">
            <span className="icon affiliate-promo__logo" style={icon('affiliate-aside-logo')} />
            <span className="affiliate-promo__name">Alpha Trader</span>
          </div>
          <p className="affiliate-promo__copy">
            {t('Where discipline meets performance. Charts, execution and tracking built to expose your edge.')}
          </p>
          {/* A new tab, so the dashboard stays where it was —
              as the sidebar's MyAlpha Quant card does. */}
          <a
            className="affiliate-promo__cta"
            href="https://alphatrader.com/"
            target="_blank"
            rel="noopener noreferrer"
            title={t('Opens alphatrader.com in a new tab')}
          >
            {t('Explore platform')}
          </a>
        </div>
      </div>
    </aside>
  );
}
